import InlineText from '../../InlineEdit/InlineText';
import InlineTextArea from '../../InlineEdit/InlineTextArea';
import { useRestaurantSettings } from '../../../hooks/useRestaurantSettings';
import {
  isClosedText,
  phoneDeskText,
  rowProblem,
} from '../../../helpers/hours';
import './hours.scss';

interface DayRow {
  day: string;
  time: string;
  closed?: boolean;
}

interface ServiceBlock {
  '@id'?: string;
  name: string;
  role: 'service' | 'repos';
  days: DayRow[];
}

const DEFAULT_SERVICES: ServiceBlock[] = [
  {
    name: 'Service du midi',
    role: 'service',
    days: [
      { day: 'Mardi', time: '12:00 – 14:00' },
      { day: 'Mercredi', time: '12:00 – 14:00' },
      { day: 'Jeudi', time: '12:00 – 14:00' },
      { day: 'Vendredi', time: '12:00 – 14:00' },
      { day: 'Samedi', time: '12:00 – 14:00' },
    ],
  },
  {
    name: 'Service du soir',
    role: 'service',
    days: [
      { day: 'Mardi', time: '19:00 – 21:30' },
      { day: 'Mercredi', time: '19:00 – 21:30' },
      { day: 'Jeudi', time: '19:00 – 21:30' },
      { day: 'Vendredi', time: '19:00 – 21:30' },
      { day: 'Samedi', time: '19:00 – 21:30' },
    ],
  },
  {
    name: 'Repos',
    role: 'repos',
    days: [
      { day: 'Dimanche', time: 'Fermé', closed: true },
      { day: 'Lundi', time: 'Fermé', closed: true },
    ],
  },
];

export const RESERVATION_SENTENCE =
  'Réservation en ligne ou par téléphone. Groupes de plus de six personnes : par téléphone.';

const PROBLEM_TEXT = {
  day: 'Jour non reconnu : écrivez le jour en toutes lettres, par exemple « Mercredi ».',
  time: 'Horaire non reconnu : écrivez par exemple « 12:00 – 14:00 » ou « Fermé ».',
};

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const View = (props: any) => {
  const { data, isEditMode, block, onChangeBlock } = props;
  const settings = useRestaurantSettings();

  const eyebrow = data.eyebrow || 'Horaires';
  const heading = data.heading || 'Quand nous trouver à table.';
  // The phone desk hours come from the restaurant settings, as in the footer.
  const hotline = settings.phone_hours
    ? `${phoneDeskText(settings.phone_hours)}.`
    : '';
  const reservationNote = data.reservation_note ?? RESERVATION_SENTENCE;

  const sourceServices: ServiceBlock[] =
    Array.isArray(data.services) && data.services.length > 0
      ? data.services
      : DEFAULT_SERVICES;

  const services = sourceServices.map((s) => ({
    ...s,
    role: s.role === 'repos' ? 'repos' : 'service',
    days: Array.isArray(s.days) ? s.days : [],
  })) as ServiceBlock[];

  const reposBlock = services.find((s) => s.role === 'repos');
  const serviceBlocks = services.filter((s) => s !== reposBlock);

  const updateServices = (next: ServiceBlock[]) => {
    onChangeBlock(block, { ...data, services: next });
  };

  const updateService = (targetIndex: number, patch: Partial<ServiceBlock>) => {
    const next = services.map((s, i) =>
      i === targetIndex ? { ...s, ...patch } : s,
    );
    updateServices(next);
  };

  const updateDay = (
    serviceIndex: number,
    dayIndex: number,
    patch: Partial<DayRow>,
  ) => {
    const target = services[serviceIndex];
    const nextDays = target.days.map((d, i) =>
      i === dayIndex ? { ...d, ...patch } : d,
    );
    updateService(serviceIndex, { days: nextDays });
  };

  const removeDay = (serviceIndex: number, dayIndex: number) => {
    const target = services[serviceIndex];
    const nextDays = target.days.filter((_, i) => i !== dayIndex);
    updateService(serviceIndex, { days: nextDays });
  };

  const addDay = (serviceIndex: number) => {
    const target = services[serviceIndex];
    const nextDays = [
      ...target.days,
      { day: '', time: '', closed: false } as DayRow,
    ];
    updateService(serviceIndex, { days: nextDays });
  };

  const removeService = (serviceIndex: number) => {
    updateServices(services.filter((_, i) => i !== serviceIndex));
  };

  const addService = () => {
    updateServices([
      ...services,
      {
        '@id': newId(),
        name: '',
        role: 'service',
        days: [{ day: '', time: '', closed: false }],
      },
    ]);
  };

  const renderService = (block: ServiceBlock, serviceIndex: number) => (
    <dl className="hours-block" key={block['@id'] ?? serviceIndex}>
      <dt className="inline-host">
        {isEditMode ? (
          <>
            <InlineText
              value={block.name}
              onChange={(name) => updateService(serviceIndex, { name })}
              renderTag="span"
              placeholder="Nom du service"
              ariaLabel="Nom du service"
            />
            <button
              type="button"
              aria-label={`Supprimer le service « ${block.name || 'sans nom'} »`}
              className="inline-remove"
              onClick={() => removeService(serviceIndex)}
            >
              ✕
            </button>
          </>
        ) : (
          block.name
        )}
      </dt>
      <dd>
        {block.days.map((row, dayIndex) => {
          const closed = row.closed || isClosedText(row.time);
          const problem =
            isEditMode && block.role !== 'repos' ? rowProblem(row) : null;
          return (
            <div
              className={`hours-day inline-host${closed ? ' closed' : ''}`}
              key={dayIndex}
            >
              {isEditMode ? (
                <>
                  <InlineText
                    value={row.day}
                    onChange={(day) =>
                      updateDay(serviceIndex, dayIndex, { day })
                    }
                    renderTag="span"
                    renderClassName="day"
                    placeholder="Jour"
                    ariaLabel="Jour"
                  />
                  <InlineText
                    value={row.time}
                    onChange={(time) =>
                      updateDay(serviceIndex, dayIndex, { time })
                    }
                    renderTag="span"
                    renderClassName="time"
                    placeholder="Horaire"
                    ariaLabel="Horaire"
                  />
                  <button
                    type="button"
                    aria-label={`Supprimer ${row.day || 'le jour'}`}
                    className="inline-remove"
                    onClick={() => removeDay(serviceIndex, dayIndex)}
                  >
                    ✕
                  </button>
                  {problem && (
                    <p className="hours-warning" role="status">
                      {PROBLEM_TEXT[problem]}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <span className="day">{row.day}</span>
                  <span className="time">{closed ? 'Fermé' : row.time}</span>
                </>
              )}
            </div>
          );
        })}
        {isEditMode && (
          <button
            type="button"
            className="inline-add hours-day-add"
            onClick={() => addDay(serviceIndex)}
          >
            + Ajouter un jour
          </button>
        )}
      </dd>
    </dl>
  );

  const serviceIndexOf = (block: ServiceBlock) => services.indexOf(block);

  return (
    <section className="hours" id="horaires">
      <div className="shell">
        <div className="hours-head">
          {isEditMode ? (
            <>
              <InlineText
                value={eyebrow}
                onChange={(val) =>
                  onChangeBlock(block, { ...data, eyebrow: val })
                }
                renderTag="p"
                renderClassName="eyebrow"
                placeholder="Surtitre"
                ariaLabel="Surtitre"
              />
              <InlineText
                value={heading}
                onChange={(val) =>
                  onChangeBlock(block, { ...data, heading: val })
                }
                renderTag="h2"
                placeholder="Titre"
                ariaLabel="Titre"
              />
            </>
          ) : (
            <>
              <p className="eyebrow">{eyebrow}</p>
              <h2>{heading}</h2>
            </>
          )}
        </div>
        <div className="hours-grid">
          {serviceBlocks.map((sb) => renderService(sb, serviceIndexOf(sb)))}
          <div className="hours-info">
            {reposBlock &&
              renderService(reposBlock, serviceIndexOf(reposBlock))}
            {isEditMode ? (
              <>
                {hotline && (
                  <p
                    className="hours-info-paragraph"
                    title="Modifiable dans les réglages du restaurant (« Permanence téléphonique »)"
                  >
                    {hotline}
                  </p>
                )}
                <InlineTextArea
                  value={reservationNote}
                  onChange={(val) =>
                    onChangeBlock(block, { ...data, reservation_note: val })
                  }
                  renderTag="p"
                  renderClassName="hours-info-paragraph"
                  placeholder="Note sur les réservations…"
                  ariaLabel="Note sur les réservations"
                />
              </>
            ) : (
              <>
                {hotline && <p className="hours-info-paragraph">{hotline}</p>}
                <p className="hours-info-paragraph">{reservationNote}</p>
              </>
            )}
          </div>
        </div>
        {isEditMode && (
          <button
            type="button"
            className="inline-add inline-add--full hours-service-add"
            onClick={addService}
          >
            + Ajouter un service
          </button>
        )}
      </div>
    </section>
  );
};

export default View;
